import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Switch,
  Tabs,
  Row,
  Col,
  Statistic,
  Progress,
  Tooltip,
  Divider,
  List,
  Badge,
  Descriptions,
  message,
  Popconfirm,
  Radio,
  Checkbox,
  Upload,
  Alert,
  Steps,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
  ExportOutlined,
  ImportOutlined,
  FilterOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const ScriptManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterScene, setFilterScene] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [testResults, setTestResults] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState(0);

  // 新增话术
  const handleAddScript = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  // 编辑话术
  const handleEditScript = (record: any) => {
    setSelectedRecord(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  // 保存话术
  const handleSaveScript = async (values: any) => {
    setLoading(true);
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (selectedRecord) {
        message.success('话术更新成功！');
      } else {
        message.success('话术创建成功！');
      }
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 删除话术
  const handleDeleteScript = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(`已删除话术：${record.scriptId}`);
    } catch (error) {
      message.error('删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 预览话术
  const handlePreviewScript = (record: any) => {
    setSelectedRecord(record);
    setPreviewModalVisible(true);
  };

  // 测试话术
  const handleTestScript = (record: any) => {
    setSelectedRecord(record);
    setTestResults([]);
    setCurrentStep(0);
    setTestModalVisible(true);
  };

  // 复制话术
  const handleCopyScript = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      message.success(`已复制话术：${record.scriptId}`);
    } catch (error) {
      message.error('复制失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的话术');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(`已删除 ${selectedRowKeys.length} 条话术`);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('批量删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 导入话术
  const handleImportScripts = () => {
    setImportModalVisible(true);
  };

  // 导出话术
  const handleExportScripts = () => {
    setExportModalVisible(true);
  };

  // 运行话术测试
  const handleRunTest = async () => {
    setLoading(true);
    try {
      // 模拟测试过程
      const steps = [
        { step: 0, title: '准备测试环境', status: 'process' },
        { step: 1, title: '加载话术模板', status: 'process' },
        { step: 2, title: '执行话术逻辑', status: 'process' },
        { step: 3, title: '验证输出结果', status: 'process' },
        { step: 4, title: '生成测试报告', status: 'finish' },
      ];
      
      for (let i = 0; i < steps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setCurrentStep(i);
        setTestResults(prev => [...prev, steps[i]]);
      }
      
      message.success('话术测试完成！');
    } catch (error) {
      message.error('测试失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 模拟话术数据
  const scriptData = [
    {
      id: 1,
      scriptId: 'MSG001',
      scriptCode: 'MSG001',
      scriptName: '欢迎语-热情友好',
      content: '🎉欢迎新朋友{username}！恭喜你加入我们的大家庭！这里有超多精彩等着你，准备好开始你的精彩之旅了吗？',
      scene: '用户注册欢迎',
      agent: '气氛活跃Bot',
      weight: 10,
      variables: ['username'],
      styles: ['ENTHUSIASTIC', 'FRIENDLY'],
      status: 'enabled',
      usageCount: 1256,
      successRate: 94.5,
      lastUsed: '2025-10-23 14:30',
      createdAt: '2025-10-20',
    },
    {
      id: 2,
      scriptId: 'MSG002',
      scriptCode: 'MSG002',
      scriptName: '游戏引导-官方友好',
      content: '📋{username}，欢迎体验{game_name}！这款游戏规则简单易懂，适合新手玩家。游戏采用随机算法，确保公平性。',
      scene: '游戏新手引导',
      agent: '产品介绍Bot',
      weight: 8,
      variables: ['username', 'game_name'],
      styles: ['OFFICIAL', 'FRIENDLY'],
      status: 'enabled',
      usageCount: 856,
      successRate: 92.3,
      lastUsed: '2025-10-23 14:25',
      createdAt: '2025-10-20',
    },
    {
      id: 3,
      scriptId: 'MSG003',
      scriptCode: 'MSG003',
      scriptName: '维护通知-官方',
      content: '⚠️重要通知：系统将于{time}进行维护升级，预计持续{duration}。请大家提前做好安排，维护期间暂停服务。',
      scene: '系统维护通知',
      agent: '客服Bot',
      weight: 10,
      variables: ['time', 'duration'],
      styles: ['OFFICIAL'],
      status: 'enabled',
      usageCount: 234,
      successRate: 98.7,
      lastUsed: '2025-10-23 14:20',
      createdAt: '2025-10-20',
    },
    {
      id: 4,
      scriptId: 'MSG004',
      scriptCode: 'MSG004',
      scriptName: '连败安慰-友好热情',
      content: '💝{username}，{game_name}连败{lose_streak}局不要灰心。我们理解你的感受，失败是成长的一部分。',
      scene: '游戏连败安慰',
      agent: '情感交流Bot',
      weight: 7,
      variables: ['username', 'game_name', 'lose_streak'],
      styles: ['FRIENDLY', 'ENTHUSIASTIC'],
      status: 'enabled',
      usageCount: 445,
      successRate: 89.2,
      lastUsed: '2025-10-23 14:15',
      createdAt: '2025-10-20',
    },
  ];

  const columns = [
    {
      title: '话术编码',
      dataIndex: 'scriptCode',
      key: 'scriptCode',
      width: 100,
      render: (_: any, record: any) => record.scriptCode || record.scriptId,
    },
    {
      title: '话术名称',
      dataIndex: 'scriptName',
      key: 'scriptName',
      width: 150,
    },
    {
      title: '话术内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content: string) => (
        <Tooltip title={content}>
          <span>{content}</span>
        </Tooltip>
      ),
    },
    {
      title: '应用场景',
      dataIndex: 'scene',
      key: 'scene',
      width: 120,
      render: (scene: string) => <Tag color="blue">{scene}</Tag>,
    },
    {
      title: '智能体',
      dataIndex: 'agent',
      key: 'agent',
      width: 120,
      render: (agent: string) => <Tag color="green">{agent}</Tag>,
    },
    {
      title: '风格',
      dataIndex: 'styles',
      key: 'styles',
      width: 150,
      render: (styles: string[]) => (
        <Space wrap>
          {styles?.map((style, index) => (
            <Tag key={index} color="purple">
              {style === 'OFFICIAL' ? '官方' : 
               style === 'ENTHUSIASTIC' ? '热情' : 
               style === 'FRIENDLY' ? '友好' : 
               style === 'FUNNY' ? '搞笑' : 
               style === 'COMPLAIN' ? '吐槽' : 
               style === 'EDGY' ? '擦边' : style}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '权重',
      dataIndex: 'weight',
      key: 'weight',
      width: 80,
      render: (weight: number) => (
        <Badge count={weight} style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: '使用次数',
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 100,
      sorter: (a: any, b: any) => a.usageCount - b.usageCount,
    },
    {
      title: '成功率',
      dataIndex: 'successRate',
      key: 'successRate',
      width: 100,
      render: (rate: number) => (
        <Progress
          percent={rate}
          size="small"
          format={() => `${rate}%`}
          strokeColor={rate >= 90 ? '#52c41a' : rate >= 80 ? '#faad14' : '#ff4d4f'}
        />
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={status === 'enabled' ? 'green' : 'red'}>
          {status === 'enabled' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="预览话术">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handlePreviewScript(record)}
            >
              预览
            </Button>
          </Tooltip>
          <Tooltip title="编辑话术">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditScript(record)}
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title="测试话术">
            <Button
              type="link"
              icon={<ExperimentOutlined />}
              onClick={() => handleTestScript(record)}
            >
              测试
            </Button>
          </Tooltip>
          <Tooltip title="复制话术">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => handleCopyScript(record)}
            >
              复制
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个话术吗？"
            onConfirm={() => handleDeleteScript(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除话术">
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
              >
                删除
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const scriptStats = [
    { title: '总话术数', value: 2580, icon: <MessageOutlined /> },
    { title: '启用话术', value: 2156, icon: <MessageOutlined /> },
    { title: '今日使用', value: 3240, icon: <MessageOutlined /> },
    { title: '平均成功率', value: '92.5%', icon: <MessageOutlined /> },
  ];

  const sceneStats = [
    { scene: '用户注册欢迎', count: 156, usage: 85 },
    { scene: '游戏新手引导', count: 89, usage: 72 },
    { scene: '系统维护通知', count: 45, usage: 91 },
    { scene: '游戏连败安慰', count: 78, usage: 68 },
    { scene: '充值成功通知', count: 67, usage: 94 },
  ];

  const handleEditSubmit = (values: any) => {
    console.log('保存话术:', values);
    setEditModalVisible(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1>话术管理</h1>
        <p>配置话术内容和应用场景，设置对话中的频率和规则，实现话术的标准化管理和智能优化</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {scriptStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <div className="stats-card">
                <div style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }}>
                  {stat.icon}
                </div>
                <div className="stats-number">{stat.value}</div>
                <div className="stats-label">{stat.title}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* 话术列表 */}
        <Col xs={24} lg={16}>
          <Card
            title="话术库"
            extra={
              <Space>
                <Tooltip title="规则配置">
                  <Button icon={<SettingOutlined />} onClick={() => setRuleModalVisible(true)}>
                    规则配置
                  </Button>
                </Tooltip>
                <Tooltip title="导入话术">
                  <Button icon={<ImportOutlined />} onClick={handleImportScripts}>
                    导入
                  </Button>
                </Tooltip>
                <Tooltip title="导出话术">
                  <Button icon={<ExportOutlined />} onClick={handleExportScripts}>
                    导出
                  </Button>
                </Tooltip>
                <Tooltip title="新增话术">
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddScript}>
                    新增话术
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input
                  placeholder="搜索话术内容或ID"
                  prefix={<SearchOutlined />}
                  style={{ width: 200 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Select 
                  placeholder="应用场景" 
                  style={{ width: 150 }}
                  value={filterScene}
                  onChange={setFilterScene}
                >
                  <Option value="all">全部场景</Option>
                  <Option value="用户注册欢迎">用户注册欢迎</Option>
                  <Option value="游戏新手引导">游戏新手引导</Option>
                  <Option value="系统维护通知">系统维护通知</Option>
                  <Option value="游戏连败安慰">游戏连败安慰</Option>
                </Select>
                <Select 
                  placeholder="智能体" 
                  style={{ width: 120 }}
                  value={filterAgent}
                  onChange={setFilterAgent}
                >
                  <Option value="all">全部智能体</Option>
                  <Option value="气氛活跃Bot">气氛活跃Bot</Option>
                  <Option value="产品介绍Bot">产品介绍Bot</Option>
                  <Option value="客服Bot">客服Bot</Option>
                  <Option value="情感交流Bot">情感交流Bot</Option>
                </Select>
                <Select placeholder="状态" style={{ width: 100 }}>
                  <Option value="all">全部状态</Option>
                  <Option value="enabled">启用</Option>
                  <Option value="disabled">禁用</Option>
                </Select>
                <Tooltip title="刷新数据">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => message.info('数据已刷新')}
                  />
                </Tooltip>
                <Tooltip title="高级筛选">
                  <Button icon={<FilterOutlined />} />
                </Tooltip>
              </Space>
            </div>

            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={scriptData}
              rowKey="id"
              scroll={{ x: 1400 }}
              pagination={{
                total: scriptData.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条话术`,
              }}
            />

            {/* 批量操作 */}
            {selectedRowKeys.length > 0 && (
              <Card style={{ marginTop: 16 }}>
                <Space>
                  <span>已选择 {selectedRowKeys.length} 条话术</span>
                  <Tooltip title="批量导出">
                    <Button icon={<ExportOutlined />} onClick={handleExportScripts}>
                      批量导出
                    </Button>
                  </Tooltip>
                  <Popconfirm
                    title={`确定要删除选中的 ${selectedRowKeys.length} 条话术吗？`}
                    onConfirm={handleBatchDelete}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="批量删除">
                      <Button icon={<DeleteOutlined />} danger>
                        批量删除
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Space>
              </Card>
            )}
          </Card>
        </Col>

        {/* 场景统计 */}
        <Col xs={24} lg={8}>
          <Card title="场景使用统计" extra={<Button type="link">查看全部</Button>}>
            <List
              size="small"
              dataSource={sceneStats}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{item.scene}</span>
                        <Badge count={item.count} style={{ backgroundColor: '#52c41a' }} />
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>话术数量: {item.count}</div>
                        <Progress percent={item.usage} size="small" />
                        <span style={{ fontSize: 12, color: '#666' }}>使用率: {item.usage}%</span>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          <Card title="话术标签" style={{ marginTop: 16 }}>
            <Space wrap>
              <Tag color="blue">欢迎</Tag>
              <Tag color="green">引导</Tag>
              <Tag color="orange">通知</Tag>
              <Tag color="purple">安慰</Tag>
              <Tag color="cyan">营销</Tag>
              <Tag color="red">警告</Tag>
              <Tag color="gold">庆祝</Tag>
              <Tag color="lime">技巧</Tag>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 编辑话术模态框 */}
      <Modal
        title={selectedRecord ? '编辑话术' : '新增话术'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Button key="preview" icon={<EyeOutlined />}>
            预览
          </Button>,
          <Button key="save" type="primary" onClick={() => form.submit()}>
            保存
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="scriptCode"
                label="话术编码"
                rules={[{ required: true, message: '请输入话术编码' }]}
              >
                <Input placeholder="如: MSG001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="scriptName"
                label="话术名称"
                rules={[{ required: true, message: '请输入话术名称' }]}
              >
                <Input placeholder="如: 欢迎语-官方风格" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="content"
            label="话术内容"
            rules={[{ required: true, message: '请输入话术内容' }]}
          >
            <TextArea
              rows={4}
              placeholder="输入话术内容，使用{变量名}表示变量"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="scene"
                label="应用场景"
                rules={[{ required: true, message: '请选择应用场景' }]}
              >
                <Select placeholder="选择应用场景">
                  <Option value="用户注册欢迎">用户注册欢迎</Option>
                  <Option value="游戏新手引导">游戏新手引导</Option>
                  <Option value="系统维护通知">系统维护通知</Option>
                  <Option value="游戏连败安慰">游戏连败安慰</Option>
                  <Option value="充值成功通知">充值成功通知</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="agent"
                label="智能体"
                rules={[{ required: true, message: '请选择智能体' }]}
              >
                <Select placeholder="选择智能体">
                  <Option value="气氛活跃Bot">气氛活跃Bot</Option>
                  <Option value="产品介绍Bot">产品介绍Bot</Option>
                  <Option value="客服Bot">客服Bot</Option>
                  <Option value="情感交流Bot">情感交流Bot</Option>
                  <Option value="搞笑Bot">搞笑Bot</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="variables"
            label="使用变量"
          >
            <Select
              mode="tags"
              placeholder="输入变量名，如: username, game_name"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="styles"
            label="关联风格"
            rules={[{ required: true, message: '请选择至少一个风格' }]}
          >
            <Select
              mode="multiple"
              placeholder="选择风格（可多选）"
              style={{ width: '100%' }}
            >
              <Option value="OFFICIAL">官方</Option>
              <Option value="ENTHUSIASTIC">热情</Option>
              <Option value="FRIENDLY">友好</Option>
              <Option value="FUNNY">搞笑</Option>
              <Option value="COMPLAIN">吐槽</Option>
              <Option value="EDGY">擦边</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 规则配置模态框 */}
      <Modal
        title="话术规则配置"
        open={ruleModalVisible}
        onCancel={() => setRuleModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setRuleModalVisible(false)}>
            取消
          </Button>,
          <Button key="save" type="primary">
            保存
          </Button>,
        ]}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="频率规则" key="1">
            <div style={{ marginBottom: 16 }}>
              <h4>全局规则</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <label>每分钟最大话术数：</label>
                  <InputNumber min={1} max={100} defaultValue={10} style={{ width: 100, marginLeft: 8 }} />
                </Col>
                <Col span={12}>
                  <label>每小时最大话术数：</label>
                  <InputNumber min={1} max={1000} defaultValue={100} style={{ width: 100, marginLeft: 8 }} />
                </Col>
              </Row>
            </div>
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <h4>场景规则</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <label>触发冷却时间：</label>
                  <InputNumber min={1} max={60} defaultValue={5} style={{ width: 100, marginLeft: 8 }} />
                  <span style={{ marginLeft: 8 }}>分钟</span>
                </Col>
                <Col span={12}>
                  <label>并发控制：</label>
                  <InputNumber min={1} max={10} defaultValue={3} style={{ width: 100, marginLeft: 8 }} />
                </Col>
              </Row>
            </div>
            <Divider />
            <div>
              <h4>话术规则</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <label>重复间隔：</label>
                  <InputNumber min={1} max={60} defaultValue={10} style={{ width: 100, marginLeft: 8 }} />
                  <span style={{ marginLeft: 8 }}>分钟</span>
                </Col>
                <Col span={12}>
                  <label>使用上限：</label>
                  <InputNumber min={1} max={1000} defaultValue={100} style={{ width: 100, marginLeft: 8 }} />
                  <span style={{ marginLeft: 8 }}>次/天</span>
                </Col>
              </Row>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="用户规则" key="2">
            <div style={{ marginBottom: 16 }}>
              <h4>单用户规则</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <label>触发间隔：</label>
                  <InputNumber min={1} max={60} defaultValue={2} style={{ width: 100, marginLeft: 8 }} />
                  <span style={{ marginLeft: 8 }}>分钟</span>
                </Col>
                <Col span={12}>
                  <label>每日上限：</label>
                  <InputNumber min={1} max={100} defaultValue={20} style={{ width: 100, marginLeft: 8 }} />
                </Col>
              </Row>
            </div>
            <Divider />
            <div>
              <h4>用户分层规则</h4>
              <List
                size="small"
                dataSource={[
                  { level: 'VIP用户', interval: 1, dailyLimit: 50 },
                  { level: '活跃用户', interval: 2, dailyLimit: 30 },
                  { level: '普通用户', interval: 5, dailyLimit: 20 },
                  { level: '新用户', interval: 3, dailyLimit: 15 },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.level}
                      description={
                        <Space>
                          <span>间隔: {item.interval}分钟</span>
                          <span>每日上限: {item.dailyLimit}次</span>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="智能体规则" key="3">
            <div>
              <h4>智能体调度规则</h4>
              <List
                size="small"
                dataSource={[
                  { agent: '气氛活跃Bot', priority: 8, maxConcurrent: 5 },
                  { agent: '产品介绍Bot', priority: 7, maxConcurrent: 3 },
                  { agent: '客服Bot', priority: 9, maxConcurrent: 10 },
                  { agent: '情感交流Bot', priority: 6, maxConcurrent: 2 },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.agent}
                      description={
                        <Space>
                          <span>优先级: {item.priority}</span>
                          <span>最大并发: {item.maxConcurrent}</span>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      {/* 预览模态框 */}
      <Modal
        title="话术预览"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        width={600}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            关闭
          </Button>,
          <Button key="test" type="primary" icon={<PlayCircleOutlined />}>
            测试
          </Button>,
        ]}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <h4>话术内容</h4>
              <div style={{ 
                background: '#f5f5f5', 
                padding: 16, 
                borderRadius: 8,
                border: '1px solid #d9d9d9'
              }}>
                {selectedRecord.content}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <h4>变量替换示例</h4>
              <div style={{ 
                background: '#e6f7ff', 
                padding: 16, 
                borderRadius: 8,
                border: '1px solid #91d5ff'
              }}>
                {selectedRecord.content
                  .replace('{username}', '张三')
                  .replace('{game_name}', '老虎机')
                  .replace('{time}', '2025-10-23 15:00')
                  .replace('{duration}', '2小时')
                  .replace('{lose_streak}', '3')
                }
              </div>
            </div>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="话术编码">{selectedRecord.scriptCode || selectedRecord.scriptId}</Descriptions.Item>
              <Descriptions.Item label="话术名称">{selectedRecord.scriptName || '-'}</Descriptions.Item>
              <Descriptions.Item label="应用场景">{selectedRecord.scene}</Descriptions.Item>
              <Descriptions.Item label="智能体">{selectedRecord.agent}</Descriptions.Item>
              <Descriptions.Item label="权重">{selectedRecord.weight}</Descriptions.Item>
              <Descriptions.Item label="使用变量">
                <Space wrap>
                  {selectedRecord.variables.map((variable: string, index: number) => (
                    <Tag key={index}>{variable}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="关联风格">
                <Space wrap>
                  {selectedRecord.styles?.map((style: string, index: number) => (
                    <Tag key={index} color="purple">
                      {style === 'OFFICIAL' ? '官方' : 
                       style === 'ENTHUSIASTIC' ? '热情' : 
                       style === 'FRIENDLY' ? '友好' : 
                       style === 'FUNNY' ? '搞笑' : 
                       style === 'COMPLAIN' ? '吐槽' : 
                       style === 'EDGY' ? '擦边' : style}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 话术测试模态框 */}
      <Modal
        title="话术测试"
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setTestModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="test" 
            type="primary" 
            icon={<ExperimentOutlined />}
            onClick={handleRunTest}
            loading={loading}
          >
            运行测试
          </Button>
        ]}
      >
        {selectedRecord && (
          <div>
            <Alert
              message="测试话术"
              description={`正在测试话术：${selectedRecord.scriptId}`}
              type="info"
              style={{ marginBottom: 16 }}
            />
            
            <Steps
              current={currentStep}
              items={[
                { title: '准备测试环境' },
                { title: '加载话术模板' },
                { title: '执行话术逻辑' },
                { title: '验证输出结果' },
                { title: '生成测试报告' },
              ]}
              style={{ marginBottom: 24 }}
            />
            
            {testResults.length > 0 && (
              <div>
                <h4>测试结果：</h4>
                <List
                  dataSource={testResults}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={item.title}
                        description={`步骤 ${item.step + 1}`}
                      />
                      <Tag color={item.status === 'finish' ? 'green' : 'blue'}>
                        {item.status === 'finish' ? '完成' : '进行中'}
                      </Tag>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 导入话术模态框 */}
      <Modal
        title="导入话术"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setImportModalVisible(false)}>
            取消
          </Button>,
          <Button key="import" type="primary" icon={<ImportOutlined />}>
            开始导入
          </Button>
        ]}
      >
        <div>
          <Alert
            message="导入说明"
            description="支持导入Excel、CSV格式的话术文件，请确保文件格式正确"
            type="info"
            style={{ marginBottom: 16 }}
          />
          
          <Upload.Dragger
            name="file"
            multiple={false}
            accept=".xlsx,.xls,.csv"
            beforeUpload={() => false}
          >
            <p className="ant-upload-drag-icon">
              <FileTextOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">支持单个文件上传，文件大小不超过10MB</p>
          </Upload.Dragger>
          
          <div style={{ marginTop: 16 }}>
            <h4>导入模板：</h4>
            <Button icon={<DownloadOutlined />} type="link">
              下载Excel模板
            </Button>
            <Button icon={<DownloadOutlined />} type="link">
              下载CSV模板
            </Button>
          </div>
        </div>
      </Modal>

      {/* 导出话术模态框 */}
      <Modal
        title="导出话术"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        width={500}
        footer={[
          <Button key="cancel" onClick={() => setExportModalVisible(false)}>
            取消
          </Button>,
          <Button key="export" type="primary" icon={<ExportOutlined />}>
            开始导出
          </Button>
        ]}
      >
        <div>
          <Form layout="vertical">
            <Form.Item label="导出格式">
              <Radio.Group defaultValue="excel">
                <Radio value="excel">Excel (.xlsx)</Radio>
                <Radio value="csv">CSV (.csv)</Radio>
                <Radio value="json">JSON (.json)</Radio>
              </Radio.Group>
            </Form.Item>
            
            <Form.Item label="导出范围">
              <Radio.Group defaultValue="all">
                <Radio value="all">全部话术</Radio>
                <Radio value="selected">已选择的话术 ({selectedRowKeys.length} 条)</Radio>
                <Radio value="filtered">当前筛选结果</Radio>
              </Radio.Group>
            </Form.Item>
            
            <Form.Item label="包含字段">
              <Checkbox.Group defaultValue={['scriptId', 'content', 'scene', 'agent', 'weight']}>
                <Checkbox value="scriptId">话术ID</Checkbox>
                <Checkbox value="content">话术内容</Checkbox>
                <Checkbox value="scene">应用场景</Checkbox>
                <Checkbox value="agent">智能体</Checkbox>
                <Checkbox value="weight">权重</Checkbox>
                <Checkbox value="usageCount">使用次数</Checkbox>
                <Checkbox value="status">状态</Checkbox>
                <Checkbox value="createdAt">创建时间</Checkbox>
              </Checkbox.Group>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default ScriptManagement;
